import { HttpEvent, HttpHandlerFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { map } from 'rxjs';

import { isManagedApiUrl } from '@core/utils/api-url';
import { environment } from '@environment/environment';

// Matches an ISO-8601 date-time WITHOUT a timezone designator (no trailing "Z" and no ±hh:mm offset).
// A time component is required, so date-only values (e.g. "2026-08-20") are deliberately left untouched.
const OFFSETLESS_DATETIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/;

function normalizeUtcInPlace(value: unknown): unknown {
  if (typeof value === 'string') {
    return OFFSETLESS_DATETIME.test(value) ? `${value}Z` : value;
  }
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) {
      value[i] = normalizeUtcInPlace(value[i]);
    }
    return value;
  }
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    for (const key of Object.keys(record)) {
      record[key] = normalizeUtcInPlace(record[key]);
    }
    return value;
  }
  return value;
}

/**
 * Stopgap for a backend contract gap: the API serializes UTC timestamps without a "Z", so the browser and
 * the Angular date pipe parse them as local time and render the wrong day/time. Until the API tags them UTC,
 * this tags offset-less ISO date-times as UTC at the HTTP boundary so every downstream consumer is correct.
 * Date-only strings are left as-is. Registered last, so responses are normalized before any caching.
 */
export function utcDateInterceptorFn(req: HttpRequest<unknown>, next: HttpHandlerFn) {
  if (!isManagedApiUrl(req.url, environment.apiUrl)) {
    return next(req);
  }
  return next(req).pipe(
    map((event: HttpEvent<unknown>) => {
      if (event instanceof HttpResponse && event.body && typeof event.body === 'object') {
        normalizeUtcInPlace(event.body);
      }
      return event;
    })
  );
}
